import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back! Redirecting to dashboard…");
      navigate("/");
    } catch (e2) {
      const status = e2.response?.status;
      if (status === 401 || status === 403) {
        setError("Incorrect email or password. Please try again.");
      } else if (status === 422) {
        setError("Please enter a valid email address and password.");
      } else if (!e2.response) {
        setError("Cannot reach the server. Make sure the backend is running.");
      } else {
        setError(formatApiError(e2.response?.data?.detail) || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Left panel — hero */}
      <div className="hidden lg:flex relative overflow-hidden bg-slate-900 text-white">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/1440504/pexels-photo-1440504.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src="/brand/logo.png" alt="CTI" className="w-14 h-14 rounded bg-white p-1 object-contain" />
            <div>
              <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>CTI Print</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-300">Job Card System</div>
            </div>
          </div>

          <div className="space-y-6 max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-400/15 border border-yellow-500/30 rounded-full text-yellow-300 text-xs uppercase tracking-widest">
              <ShieldCheck className="w-3.5 h-3.5" /> Secure Access
            </div>
            <h1 className="text-4xl xl:text-5xl font-semibold leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
              Print smarter. <br />
              Track every job end-to-end.
            </h1>
            <p className="text-slate-300 text-base leading-relaxed">
              Auto job card numbering, material issue tracking, machine logs, wastage & packing —
              all in one industrial-grade dashboard.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
              {[
                ["Auto", "Job Card No."],
                ["A4", "Print-ready"],
                ["QR", "Traceability"],
              ].map(([a, b]) => (
                <div key={a}>
                  <div className="text-2xl font-mono text-yellow-400">{a}</div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400">{b}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-slate-400">© {new Date().getFullYear()} CTI Printing Line</div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <img src="/brand/logo.png" alt="CTI" className="w-12 h-12 object-contain" />
            <div>
              <div className="text-base font-semibold">CTI Print</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Job Card System</div>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-yellow-600 font-semibold mb-2">Welcome back</div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
              Sign in to your account
            </h2>
            <p className="text-slate-500 mt-2 text-sm">
              Use your credentials to access the production dashboard.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="h-11 bg-white"
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-600">Password</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="h-11 bg-white"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
